import { useEffect } from 'react';

import { chronicleBridge } from '@/tauri/chronicle';
import { listenChronicleObservability } from '@/tauri/chronicle/events';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { remapScribeRemoteObservabilityEvent } from '@/utils/analytics/remap-scribe-remote-observability';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

/**
 * Forwards `chronicle:observability` breadcrumbs (Rust Chronicle live path —
 * per-chunk upload telemetry) to the Datadog log index; the live twin of the
 * `bulk_download:observability` forwarder in `useChronicleBulkSyncEvents`.
 *
 * Remaps legacy Stream `hardware_chunk_upload_*` / `native_chunk_upload_*`
 * names onto `scribe.remote.*`. Chunk-live already emits the new keys from
 * Rust.
 *
 * Mounted once by ChronicleStateProvider.
 */
export function useChronicleObservabilityListener() {
  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let unlistenFn: (() => void) | null = null;
    // Same teardown-race guard as the auto_session_created listener (APP-7607).
    let cancelled = false;

    const setup = async () => {
      try {
        const unlisten = await listenChronicleObservability((event) => {
          const { eventName, level, attributes } = event.payload;
          // Strip `device_file_name` for Datadog only — leave any
          // caller-side identity keys on the raw FW string.
          const stripped = {
            ...attributes,
            device_file_name: stripDeviceFileNameForAnalytics(
              typeof attributes.device_file_name === 'string'
                ? attributes.device_file_name
                : undefined
            ),
          };
          const remapped = remapScribeRemoteObservabilityEvent(eventName, stripped);
          if (remapped === null) return;
          logHardwareDatadogEvent(remapped.eventName, remapped.attributes, level);
        });

        if (cancelled) {
          unlisten();
          return;
        }
        unlistenFn = unlisten;
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle observability listener',
          error,
        });
      }
    };

    void setup();

    return () => {
      cancelled = true;
      unlistenFn?.();
      unlistenFn = null;
    };
  }, []);
}
